export const MAP_TYPES = {
  ROADMAP: "roadmap",
  SATELLITE: "satellite",
  HYBRID: "hybrid",
  TERRAIN: "terrain",
} as const;

export type MapType = (typeof MAP_TYPES)[keyof typeof MAP_TYPES];

export interface WeatherLayerOption {
  type: "precipitation" | "clouds" | "temperature" | "wind" | "pressure";
  isVisible: boolean;
}

export interface MapOptions {
  currentMapType: MapType;
  isFullscreen: boolean;
  showClusters: boolean;
  showVehicles: boolean;
  showShops: boolean;
  showTraffic: boolean;
  showTimeZones: boolean;
  showRoute: boolean;
  showAnyTrackVehicles: boolean;
  weatherLayers: WeatherLayerOption[];
  zoomSensitivity: number;
}

export interface MapControlsProps {
  options: MapOptions;
  onMapTypeChange: (type: MapType) => void;
  onFullscreenToggle: () => void;
  onClustersToggle: () => void;
  onVehiclePinsToggle: () => void;
  onShopsToggle: () => void;
  onTrafficToggle: () => void;
  onTimeZonesToggle: () => void;
  onAnyTrackToggle: () => void;
  onWeatherLayerChange: (index: number, option: WeatherLayerOption) => void;
  onZoomSensitivityChange: (sensitivity: number) => void;
}

export interface TimeZone {
  id: string;
  name: string;
  abbreviation: string;
  color: string;
  borderColor: string;
  states: string[];
}

export const TIME_ZONES: TimeZone[] = [
  {
    id: "PT",
    name: "Pacific Time",
    abbreviation: "PT",
    color: "rgba(144, 202, 249, 0.4)",
    borderColor: "rgba(30, 136, 229, 0.8)",
    states: ["CA", "WA", "OR", "NV"],
  },
  {
    id: "MT",
    name: "Mountain Time",
    abbreviation: "MT",
    color: "rgba(129, 199, 132, 0.4)",
    borderColor: "rgba(67, 160, 71, 0.8)",
    states: ["MT", "ID", "WY", "UT", "CO", "AZ", "NM"],
  },
  {
    id: "CT",
    name: "Central Time",
    abbreviation: "CT",
    color: "rgba(255, 183, 77, 0.4)",
    borderColor: "rgba(251, 140, 0, 0.8)",
    states: [
      "ND",
      "SD",
      "NE",
      "KS",
      "OK",
      "TX",
      "MN",
      "IA",
      "MO",
      "AR",
      "LA",
      "WI",
      "IL",
      "MS",
      "AL",
      "TN",
    ],
  },
  {
    id: "ET",
    name: "Eastern Time",
    abbreviation: "ET",
    color: "rgba(229, 115, 115, 0.4)",
    borderColor: "rgba(211, 47, 47, 0.8)",
    states: [
      "MI",
      "IN",
      "OH",
      "KY",
      "GA",
      "FL",
      "SC",
      "NC",
      "VA",
      "WV",
      "MD",
      "DE",
      "NJ",
      "PA",
      "NY",
      "CT",
      "RI",
      "MA",
      "VT",
      "NH",
      "ME",
    ],
  },
];

export const STATE_CODE_MAP: { [key: string]: string } = {
  "01": "AL", // Alabama
  "02": "AK", // Alaska
  "04": "AZ", // Arizona
  "05": "AR", // Arkansas
  "06": "CA", // California
  "08": "CO", // Colorado
  "09": "CT", // Connecticut
  "10": "DE", // Delaware
  "11": "DC", // District of Columbia
  "12": "FL", // Florida
  "13": "GA", // Georgia
  "15": "HI", // Hawaii
  "16": "ID", // Idaho
  "17": "IL", // Illinois
  "18": "IN", // Indiana
  "19": "IA", // Iowa
  "20": "KS", // Kansas
  "21": "KY", // Kentucky
  "22": "LA", // Louisiana
  "23": "ME", // Maine
  "24": "MD", // Maryland
  "25": "MA", // Massachusetts
  "26": "MI", // Michigan
  "27": "MN", // Minnesota
  "28": "MS", // Mississippi
  "29": "MO", // Missouri
  "30": "MT", // Montana
  "31": "NE", // Nebraska
  "32": "NV", // Nevada
  "33": "NH", // New Hampshire
  "34": "NJ", // New Jersey
  "35": "NM", // New Mexico
  "36": "NY", // New York
  "37": "NC", // North Carolina
  "38": "ND", // North Dakota
  "39": "OH", // Ohio
  "40": "OK", // Oklahoma
  "41": "OR", // Oregon
  "42": "PA", // Pennsylvania
  "44": "RI", // Rhode Island
  "45": "SC", // South Carolina
  "46": "SD", // South Dakota
  "47": "TN", // Tennessee
  "48": "TX", // Texas
  "49": "UT", // Utah
  "50": "VT", // Vermont
  "51": "VA", // Virginia
  "53": "WA", // Washington
  "54": "WV", // West Virginia
  "55": "WI", // Wisconsin
  "56": "WY", // Wyoming
};
